import React, { useState, useEffect, useCallback, useMemo } from "react";
import { toast, Toaster } from "react-hot-toast";
import {
  Package,
  Pencil,
  Trash2,
  RotateCcw,
  Eye,
  Plus,
  Tag,
  DollarSign,
} from "lucide-react";
import "./MaterialsManagement.css";
import { appendProductionInstanceScope } from "../productionRequestScope";

const BASE_URL = process.env.REACT_APP_NODEJS_SERVER_URL;
const fetch = (input, init) => window.fetch(appendProductionInstanceScope(input, {
  pathname: window.location.pathname,
  apiBase: BASE_URL,
}), init);
const PAGE_SIZE = 50;

const fmt = (v) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(v ?? 0);

const fmtSeconds = (s) => {
  if (!s) return "—";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return (
    [h && `${h}h`, m && `${m}m`, sec && `${sec}s`].filter(Boolean).join(" ") ||
    "0s"
  );
};

const ITEM_TYPE_LABELS = {
  raw_material: "Raw Material",
  consumable: "Consumable",
  semi_finished: "Semi-Finished",
  packaging: "Packaging",
  tool: "Tool",
  other: "Other",
};
const PROCUREMENT_LABELS = {
  purchase: "Purchase",
  in_house: "In-House",
  both: "Both",
};

const EMPTY_FORM = {
  name: "",
  item_code: "",
  item_type: "",
  item_group: "",
  barcode: "",
  procurement_type: "purchase",
  base_unit: "",
  alt_unit: "",
  conversion_factor: "1",
  size_variant: "",
  item_cost: "",
  currency: "CAD",
  min_order_qty: "0",
  reorder_level: "0",
  reorder_qty: "0",
  prod_time_seconds: "0",
  description: "",
  internal_notes: "",
  is_active: true,
};

const ALL_COLUMNS = [
  { id: "name", label: "Name / Code", always: true },
  { id: "item_type", label: "Type", default: true },
  { id: "item_group", label: "Group", default: false },
  { id: "barcode", label: "Barcode", default: false },
  { id: "procurement_type", label: "Procurement", default: false },
  { id: "base_unit", label: "Base Unit", default: true },
  { id: "alt_unit", label: "Alt Unit", default: false },
  { id: "item_cost", label: "Cost", default: true },
  { id: "currency", label: "Currency", default: false },
  // { id: "min_order_qty", label: "Min Order", default: false },
  // { id: "reorder_level", label: "Reorder Level", default: false },
  // { id: "reorder_qty", label: "Reorder Qty", default: false },
  // { id: "prod_time_seconds", label: "Prod. Time", default: false },
  { id: "size_variant", label: "Size Variant", default: false },
  { id: "is_active", label: "Status", always: true },
];

const DEFAULT_VISIBILITY = Object.fromEntries(
  ALL_COLUMNS.map((c) => [c.id, c.always || c.default || false]),
);

const MaterialsManagement = () => {
  const [materials, setMaterials] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [meta, setMeta] = useState({ item_types: [], procurement_types: [] });
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [filterItemType, setFilterItemType] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // View modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  // Archive modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Restore modal
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreId, setRestoreId] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);

  const [showAuditModal, setShowAuditModal] = useState(false);
const [auditMaterialId, setAuditMaterialId] = useState(null);
const [auditLogs, setAuditLogs] = useState([]);
const [auditLoading, setAuditLoading] = useState(false);

  const authHeader = {
    Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
  };

  const [colVisibility, setColVisibility] = useState(DEFAULT_VISIBILITY);
  const [showColPicker, setShowColPicker] = useState(false);

  useEffect(() => {
    if (!showColPicker) return;
    const handler = (e) => {
      if (!e.target.closest(".col-picker-wrapper")) setShowColPicker(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showColPicker]);

  // Fetch meta dropdowns once on mount
  useEffect(() => {
    fetchMeta();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchQuery, showInactive, filterItemType]);

  // Fetch data when page/filters change
  useEffect(() => {
    fetchMaterials();
  }, [showInactive, filterItemType, page, searchQuery]);

  // Escape key handler
  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingItem(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "Escape") return;
      if (showModal) closeModal();
      if (showViewModal) setShowViewModal(false);
      if (showDeleteModal) setShowDeleteModal(false);
      if (showRestoreModal) setShowRestoreModal(false);
      if (showAuditModal) setShowAuditModal(false);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [showModal, showViewModal, showDeleteModal, showRestoreModal, showAuditModal, closeModal]);

  // API helpers
  const fetchMeta = async () => {
    try {
      const res = await fetch(`${BASE_URL}/production/materials/meta`, {
        headers: authHeader,
      });
      const data = await res.json();
      if (data.success)
        setMeta({
          item_types: data.item_types,
          procurement_types: data.procurement_types,
        });
    } catch {}
  };

  const fetchAuditLogs = async (materialId) => {
  try {
    setAuditLoading(true);
    setAuditLogs([]);
    const res = await fetch(`${BASE_URL}/production/materials/${materialId}/audit`, {
      headers: authHeader,
    });
    const data = await res.json();
    if (data.success) setAuditLogs(data.data);
    else toast.error("Failed to load audit logs");
  } catch {
    toast.error("Server error");
  } finally {
    setAuditLoading(false);
  }
};

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const p = new URLSearchParams();
      if (showInactive) p.append("inactive", "1");
      if (searchQuery.trim()) p.append("search", searchQuery.trim());
      if (filterItemType) p.append("item_type", filterItemType);
      p.append("page", String(page));
      p.append("pageSize", String(PAGE_SIZE));

      const [res, resAll] = await Promise.all([
        fetch(`${BASE_URL}/production/materials?${p}`, { headers: authHeader }),
        fetch(`${BASE_URL}/production/materials?inactive=1&pageSize=200`, {
          headers: authHeader,
        }),
      ]);
      const [data, dataAll] = await Promise.all([res.json(), resAll.json()]);

      if (data.success) {
        setMaterials(data.data);
        setTotalCount(data.total);
        setTotalPages(data.totalPages);
      } else toast.error(data.message || "Failed to load materials");

      if (dataAll.success) setAllMaterials(dataAll.data);
    } catch {
      toast.error("Server error");
    } finally {
      setLoading(false);
    }
  };

  // Validation
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Material name is required";
    if (!formData.item_type) errors.item_type = "Item type is required";
    if (formData.item_cost !== "" && Number(formData.item_cost) < 0)
      errors.item_cost = "Cost must be a positive number";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setSubmitting(true);
      const isEdit = Boolean(editingItem);
      const url = isEdit
        ? `${BASE_URL}/production/materials/${editingItem.id}`
        : `${BASE_URL}/production/materials`;

      const res = await fetch(url, {
        method: isEdit ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          name: formData.name.trim(),
          item_code: formData.item_code.trim() || null,
          item_type: formData.item_type,
          item_group: formData.item_group.trim() || null,
          barcode: formData.barcode.trim() || null,
          procurement_type: formData.procurement_type,
          base_unit: formData.base_unit.trim() || null,
          alt_unit: formData.alt_unit.trim() || null,
          conversion_factor:
            formData.conversion_factor !== ""
              ? Number(formData.conversion_factor)
              : 1,
          size_variant: formData.size_variant.trim() || null,
          item_cost: formData.item_cost !== "" ? Number(formData.item_cost) : 0,
          currency: formData.currency.trim() || "CAD",
          min_order_qty: Number(formData.min_order_qty) || 0,
          reorder_level: Number(formData.reorder_level) || 0,
          reorder_qty: Number(formData.reorder_qty) || 0,
          prod_time_seconds: Number(formData.prod_time_seconds) || 0,
          description: formData.description.trim() || null,
          internal_notes: formData.internal_notes.trim() || null,
          is_active: formData.is_active ? 1 : 0,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(isEdit ? "Material updated" : "Material created");
        fetchMaterials();
        closeModal();
      } else {
        // Server returns field-level errors (409 duplicates)
        if (data.field)
          setFormErrors((p) => ({ ...p, [data.field]: data.message }));
        else toast.error(data.message || "Failed to save");
      }
    } catch {
      toast.error("Server error");
    } finally {
      setSubmitting(false);
    }
  };

  // Archive
  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      const res = await fetch(`${BASE_URL}/production/materials/${deleteId}`, {
        method: "DELETE",
        headers: authHeader,
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Material archived");
        fetchMaterials();
      } else toast.error(data.message || "Failed to archive");
    } catch {
      toast.error("Server error");
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  // Restore
  const handleRestore = async () => {
    try {
      setRestoreLoading(true);
      const res = await fetch(
        `${BASE_URL}/production/materials/${restoreId}/restore`,
        {
          method: "PATCH",
          headers: authHeader,
        },
      );
      const data = await res.json();
      if (data.success) {
        toast.success("Material restored");
        fetchMaterials();
      } else toast.error(data.message || "Failed to restore");
    } catch {
      toast.error("Server error");
    } finally {
      setRestoreLoading(false);
      setShowRestoreModal(false);
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      item_code: item.item_code || "",
      item_type: item.item_type || "",
      item_group: item.item_group || "",
      barcode: item.barcode || "",
      procurement_type: item.procurement_type || "purchase",
      base_unit: item.base_unit || "",
      alt_unit: item.alt_unit || "",
      conversion_factor: String(item.conversion_factor ?? 1),
      size_variant: item.size_variant || "",
      item_cost: item.item_cost ?? "",
      currency: item.currency || "CAD",
      min_order_qty: String(item.min_order_qty ?? 0),
      reorder_level: String(item.reorder_level ?? 0),
      reorder_qty: String(item.reorder_qty ?? 0),
      prod_time_seconds: String(item.prod_time_seconds ?? 0),
      description: item.description || "",
      internal_notes: item.internal_notes || "",
      is_active: item.is_active === 1 || item.is_active === true,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleSort = (col) => {
    if (sortColumn === col)
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortColumn(col);
      setSortDirection("asc");
    }
  };

  const sortedMaterials = useMemo(() => {
    return [...materials].sort((a, b) => {
      const av = (a[sortColumn] ?? "").toString().toLowerCase();
      const bv = (b[sortColumn] ?? "").toString().toLowerCase();
      return sortDirection === "asc"
        ? av.localeCompare(bv)
        : bv.localeCompare(av);
    });
  }, [materials, sortColumn, sortDirection]);

  const toggleRow = (id) =>
    setSelectedRows((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
    );
  const toggleAllRows = () =>
    setSelectedRows((p) =>
      p.length === sortedMaterials.length
        ? []
        : sortedMaterials.map((m) => m.id),
    );

  // Stats (always from allMaterials ignores current filter)
  const stats = useMemo(
    () => ({
      total: allMaterials.length,
      active: allMaterials.filter(
        (m) => m.is_active === 1 || m.is_active === true,
      ).length,
      inactive: allMaterials.filter(
        (m) => m.is_active === 0 || m.is_active === false,
      ).length,
      avgCost: allMaterials.length
        ? (
            allMaterials.reduce((s, m) => s + Number(m.item_cost || 0), 0) /
            allMaterials.length
          ).toFixed(2)
        : "0.00",
    }),
    [allMaterials],
  );

  const isActive = (item) => item.is_active === 1 || item.is_active === true;

  const SortIcon = ({ col }) => (
    <svg
      className={`sort-icon${sortColumn === col ? " active" : ""}${
        sortColumn === col && sortDirection === "desc" ? " desc" : ""
      }`}
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
    >
      <path
        d="M7 3.5V10.5M7 10.5L10.5 7M7 10.5L3.5 7"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const setField = (key) => (e) =>
    setFormData((p) => ({ ...p, [key]: e.target.value }));

  return (
    <div className="materials-container">
      <Toaster position="top-right" />

      <div className="page-header">
        <div>
          <h1 className="page-title">Materials Management</h1>
          <p className="page-subtitle">
            Manage raw materials, costs, and units for production
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={() => {
            setEditingItem(null);
            setFormData(EMPTY_FORM);
            setFormErrors({});
            setShowModal(true);
          }}
        >
          <Plus size={15} /> Add Material
        </button>
      </div>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Materials</div>
          <div className="stat-value">{stats.total}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active</div>
          <div className="stat-value stat-success">{stats.active}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Inactive</div>
          <div className="stat-value stat-warning">{stats.inactive}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Avg Cost</div>
          <div className="stat-value stat-info" style={{ fontSize: 20 }}>
            {fmt(stats.avgCost)}
          </div>
        </div>
      </div>

      <div className="controls-bar">
        <div className="controls-left">
          <div className="search-box">
            <svg
              className="search-icon"
              width="16"
              height="16"
              viewBox="0 0 18 18"
              fill="none"
            >
              <path
                d="M8.25 14.25C11.5637 14.25 14.25 11.5637 14.25 8.25C14.25 4.93629 11.5637 2.25 8.25 2.25C4.93629 2.25 2.25 4.93629 2.25 8.25C2.25 11.5637 4.93629 14.25 8.25 14.25Z"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M15.75 15.75L12.4875 12.4875"
                stroke="currentColor"
                strokeWidth="1.5"
              />
            </svg>
            <input
              type="text"
              className="search-input"
              placeholder="Search by name, code, or barcode…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => setSearchQuery("")}
              >
                ×
              </button>
            )}
          </div>

          <select
            className="filter-select"
            value={filterItemType}
            onChange={(e) => setFilterItemType(e.target.value)}
          >
            <option value="">All Types</option>
            {meta.item_types.map((t) => (
              <option key={t} value={t}>
                {ITEM_TYPE_LABELS[t] || t}
              </option>
            ))}
          </select>
        </div>
        {/* <div className="controls-right">
          <label className="archive-toggle-pill">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            <span className="pill-slider" /><span className="pill-text">Show Inactive</span>
          </label>
        </div> */}

        <div className="controls-right">
          <div className="col-picker-wrapper">
            <button
              className="btn-secondary"
              style={{ gap: 6, fontSize: 13 }}
              onClick={() => setShowColPicker((p) => !p)}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <rect
                  x="1"
                  y="1"
                  width="6"
                  height="6"
                  rx="1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <rect
                  x="9"
                  y="1"
                  width="6"
                  height="6"
                  rx="1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <rect
                  x="1"
                  y="9"
                  width="6"
                  height="6"
                  rx="1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <rect
                  x="9"
                  y="9"
                  width="6"
                  height="6"
                  rx="1"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
              </svg>
              Columns
            </button>

            {showColPicker && (
              <div className="col-picker-dropdown">
                {/* <div className="col-picker-header">
          <span>Toggle Columns</span>
          <button className="col-picker-reset" onClick={() => setColVisibility(DEFAULT_VISIBILITY)}>
            Reset
          </button>
        </div> */}

                <div className="col-picker-header">
                  <span>Toggle Columns</span>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="col-picker-reset"
                      onClick={() =>
                        setColVisibility(
                          Object.fromEntries(
                            ALL_COLUMNS.map((c) => [c.id, true]),
                          ),
                        )
                      }
                    >
                      All
                    </button>
                    <span style={{ color: "#e2e8f0" }}>|</span>
                    <button
                      className="col-picker-reset"
                      onClick={() => setColVisibility(DEFAULT_VISIBILITY)}
                    >
                      Reset
                    </button>
                  </div>
                </div>
                {ALL_COLUMNS.filter((c) => !c.always).map((col) => (
                  <label key={col.id} className="col-picker-item">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={colVisibility[col.id] || false}
                      onChange={(e) =>
                        setColVisibility((p) => ({
                          ...p,
                          [col.id]: e.target.checked,
                        }))
                      }
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          <label className="archive-toggle-pill">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span className="pill-slider" />
            <span className="pill-text">Show Inactive</span>
          </label>
        </div>
      </div>

      {selectedRows.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="bulk-count">{selectedRows.length} selected</span>
          <div className="bulk-buttons">
            <button className="btn-bulk" onClick={() => setSelectedRows([])}>
              Clear Selection
            </button>
          </div>
        </div>
      )}

      <div className="table-card">
        {loading ? (
          <div
            style={{ padding: "48px", textAlign: "center", color: "#94a3b8" }}
          >
            <div
              className="spinner-border spinner-border-sm"
              style={{ width: 20, height: 20 }}
            />
            <div style={{ marginTop: 10, fontSize: 13 }}>
              Loading materials…
            </div>
          </div>
        ) : sortedMaterials.length === 0 ? (
          <div className="empty-state">
            <Package size={48} className="empty-icon" />
            <h3 className="empty-title">No materials found</h3>
            <p className="empty-text">
              {searchQuery
                ? "Try adjusting your search."
                : "Get started by adding your first material."}
            </p>
            {!searchQuery && (
              <button
                className="btn-primary"
                onClick={() => setShowModal(true)}
              >
                <Plus size={15} /> Add Material
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="premium-table">
              <thead>
                <tr>
                  <th className="th-checkbox">
                    <input
                      type="checkbox"
                      className="checkbox"
                      checked={
                        selectedRows.length === sortedMaterials.length &&
                        sortedMaterials.length > 0
                      }
                      onChange={toggleAllRows}
                    />
                  </th>
                  <th className="sortable" onClick={() => handleSort("name")}>
                    <div className="th-content">
                      Name / Code <SortIcon col="name" />
                    </div>
                  </th>
                  {colVisibility.item_type && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("item_type")}
                    >
                      <div className="th-content">
                        Type <SortIcon col="item_type" />
                      </div>
                    </th>
                  )}
                  {colVisibility.item_group && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("item_group")}
                    >
                      <div className="th-content">
                        Group <SortIcon col="item_group" />
                      </div>
                    </th>
                  )}
                  {colVisibility.barcode && <th>Barcode</th>}
                  {colVisibility.procurement_type && <th>Procurement</th>}
                  {colVisibility.base_unit && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("base_unit")}
                    >
                      <div className="th-content">
                        Base Unit <SortIcon col="base_unit" />
                      </div>
                    </th>
                  )}
                  {colVisibility.alt_unit && <th>Alt Unit</th>}
                  {colVisibility.item_cost && (
                    <th
                      className="sortable"
                      onClick={() => handleSort("item_cost")}
                    >
                      <div className="th-content">
                        Cost <SortIcon col="item_cost" />
                      </div>
                    </th>
                  )}
                  {colVisibility.currency && <th>Currency</th>}
                  {/* {colVisibility.min_order_qty && <th>Min Order</th>}
                  {colVisibility.reorder_level && <th>Reorder Lvl</th>}
                  {colVisibility.reorder_qty && <th>Reorder Qty</th>}
                  {colVisibility.prod_time_seconds && <th>Prod. Time</th>}
                  {colVisibility.size_variant && <th>Size</th>} */}
                  <th>Status</th>
                  <th style={{ textAlign: "center", minWidth: 110 }}>
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody>
                {sortedMaterials.map((item) => (
                  <tr
                    key={item.id}
                    className={selectedRows.includes(item.id) ? "selected" : ""}
                  >
                    <td style={{ textAlign: "center" }}>
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={selectedRows.includes(item.id)}
                        onChange={() => toggleRow(item.id)}
                      />
                    </td>
                    {/* Always: Name */}
                    <td>
                      <div className="material-info">
                        <span className="material-name">{item.name}</span>
                        {item.item_code && (
                          <span className="material-code">
                            {item.item_code}
                          </span>
                        )}
                      </div>
                    </td>
                    {colVisibility.item_type && (
                      <td>
                        {item.item_type ? (
                          <span className={`type-badge type-${item.item_type}`}>
                            {ITEM_TYPE_LABELS[item.item_type] || item.item_type}
                          </span>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                    )}
                    {colVisibility.item_group && (
                      <td>
                        <span className="text-medium">
                          {item.item_group || (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </span>
                      </td>
                    )}
                    {colVisibility.barcode && (
                      <td>
                        {item.barcode ? (
                          <span className="material-code">{item.barcode}</span>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                    )}
                    {colVisibility.procurement_type && (
                      <td>
                        <span className="unit-badge">
                          {PROCUREMENT_LABELS[item.procurement_type] || "—"}
                        </span>
                      </td>
                    )}
                    {colVisibility.base_unit && (
                      <td>
                        {item.base_unit ? (
                          <span className="unit-badge">{item.base_unit}</span>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                    )}
                    {colVisibility.alt_unit && (
                      <td>
                        {item.alt_unit ? (
                          <span className="unit-badge">
                            {item.alt_unit}
                            {item.conversion_factor !== 1 && (
                              <span style={{ color: "#94a3b8", fontSize: 11 }}>
                                {" "}
                                ×{item.conversion_factor}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span style={{ color: "#cbd5e1" }}>—</span>
                        )}
                      </td>
                    )}
                    {colVisibility.item_cost && (
                      <td>
                        <span className="cost-value">
                          {fmt(item.item_cost)}
                        </span>
                      </td>
                    )}
                    {colVisibility.currency && (
                      <td>
                        <span className="unit-badge">
                          {item.currency || "—"}
                        </span>
                      </td>
                    )}
                    {colVisibility.min_order_qty && (
                      <td>
                        <span className="text-medium">
                          {item.min_order_qty || "—"}
                        </span>
                      </td>
                    )}
                    {colVisibility.reorder_level && (
                      <td>
                        <span className="text-medium">
                          {item.reorder_level || "—"}
                        </span>
                      </td>
                    )}
                    {colVisibility.reorder_qty && (
                      <td>
                        <span className="text-medium">
                          {item.reorder_qty || "—"}
                        </span>
                      </td>
                    )}
                    {colVisibility.prod_time_seconds && (
                      <td>
                        <span className="text-medium">
                          {fmtSeconds(item.prod_time_seconds)}
                        </span>
                      </td>
                    )}
                    {colVisibility.size_variant && (
                      <td>
                        <span className="text-medium">
                          {item.size_variant || "—"}
                        </span>
                      </td>
                    )}
                    {/* Always: Status */}
                    <td>
                      <span
                        className={`status-badge ${
                          isActive(item) ? "active" : "inactive"
                        }`}
                      >
                        <span className="status-dot" />
                        {isActive(item) ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {/* Always: Actions */}
                    <td
                      style={{ textAlign: "center", verticalAlign: "middle" }}
                    >
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          title="View"
                          onClick={() => {
                            setViewItem(item);
                            setShowViewModal(true);
                          }}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="btn-icon"
                          title="Edit"
                          onClick={() => openEdit(item)}
                        >
                          <Pencil size={14} />
                        </button>

                        <button
  className="btn-icon"
  title="Audit Trail"
  onClick={() => {
    setAuditMaterialId(item.id);
    setShowAuditModal(true);
    fetchAuditLogs(item.id);
  }}
>

  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8"/>
    <path d="M12 7v5l3 3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
</button>

                        
                        {isActive(item) ? (
                          <button
                            className="btn-icon btn-danger"
                            title="Archive"
                            onClick={() => {
                              setDeleteId(item.id);
                              setShowDeleteModal(true);
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        ) : (
                          <button
                            className="btn-icon btn-success"
                            title="Restore"
                            onClick={() => {
                              setRestoreId(item.id);
                              setShowRestoreModal(true);
                            }}
                          >
                            <RotateCcw size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-bar">
                <span className="pagination-info">
                  Showing {(page - 1) * PAGE_SIZE + 1}–
                  {Math.min(page * PAGE_SIZE, totalCount)} of {totalCount}
                </span>
                <div className="pagination-controls">
                  <button
                    className="btn-page"
                    disabled={page === 1}
                    onClick={() => setPage(1)}
                  >
                    «
                  </button>
                  <button
                    className="btn-page"
                    disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    ‹
                  </button>
                  <span className="page-current">
                    {page} / {totalPages}
                  </span>
                  <button
                    className="btn-page"
                    disabled={page === totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    ›
                  </button>
                  <button
                    className="btn-page"
                    disabled={page === totalPages}
                    onClick={() => setPage(totalPages)}
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.className === "modal-overlay") closeModal();
          }}
        >
          <div className="modal-content" style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <div className="modal-header-content">
                <h2 className="modal-title">
                  {editingItem ? "Edit Material" : "Add Material"}
                </h2>
                <p className="modal-subtitle">
                  {editingItem
                    ? "Update material details"
                    : "Add a new raw material to your catalog"}
                </p>
              </div>
              <button className="btn-close" onClick={closeModal}>
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Section 1: Identity */}
                <div className="form-section">
                  <h3 className="section-title">
                    <Package size={13} className="section-icon" /> Basic
                    Information
                  </h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Name <span className="required">*</span>
                      </label>
                      <input
                        className={`form-control${
                          formErrors.name ? " error" : ""
                        }`}
                        placeholder="e.g. Cotton Thread"
                        value={formData.name}
                        onChange={setField("name")}
                      />
                      {formErrors.name && (
                        <span className="form-error">{formErrors.name}</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Item Code</label>
                      <input
                        className={`form-control${
                          formErrors.item_code ? " error" : ""
                        }`}
                        placeholder="e.g. MTL-001"
                        value={formData.item_code}
                        onChange={setField("item_code")}
                      />
                      {formErrors.item_code && (
                        <span className="form-error">
                          {formErrors.item_code}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Item Type <span className="required">*</span>
                      </label>
                      <select
                        className={`form-control${
                          formErrors.item_type ? " error" : ""
                        }`}
                        value={formData.item_type}
                        onChange={setField("item_type")}
                      >
                        <option value="">Select type…</option>
                        {meta.item_types.map((t) => (
                          <option key={t} value={t}>
                            {ITEM_TYPE_LABELS[t] || t}
                          </option>
                        ))}
                      </select>
                      {formErrors.item_type && (
                        <span className="form-error">
                          {formErrors.item_type}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Item Group</label>
                      <input
                        className="form-control"
                        placeholder="e.g. Fabric, Chemicals"
                        value={formData.item_group}
                        onChange={setField("item_group")}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Barcode</label>
                      <input
                        className="form-control"
                        placeholder="Scan or enter barcode"
                        value={formData.barcode}
                        onChange={setField("barcode")}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Procurement Type</label>
                      <select
                        className="form-control"
                        value={formData.procurement_type}
                        onChange={setField("procurement_type")}
                      >
                        {meta.procurement_types.map((t) => (
                          <option key={t} value={t}>
                            {PROCUREMENT_LABELS[t] || t}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Section 2: Units */}
                <div className="form-section">
                  <h3 className="section-title">
                    <Tag size={13} className="section-icon" /> Units &
                    Measurement
                  </h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Base Unit</label>
                      <input
                        className="form-control"
                        placeholder="e.g. kg, meter, pcs"
                        value={formData.base_unit}
                        onChange={setField("base_unit")}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Alt Unit</label>
                      <input
                        className="form-control"
                        placeholder="e.g. lbs, yard"
                        value={formData.alt_unit}
                        onChange={setField("alt_unit")}
                      />
                    </div>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Conversion Factor</label>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        className="form-control"
                        placeholder="1"
                        value={formData.conversion_factor}
                        onChange={setField("conversion_factor")}
                      />
                      <span className="form-hint">
                        Alt units per 1 base unit
                      </span>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Size Variant</label>
                      <input
                        className="form-control"
                        placeholder="e.g. 5mm, Large"
                        value={formData.size_variant}
                        onChange={setField("size_variant")}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Costing */}
                <div className="form-section">
                  <h3 className="section-title">
                    <DollarSign size={13} className="section-icon" /> Costing
                  </h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Cost per Unit</label>
                      <input
                        type="number"
                        min="0"
                        step="0.0001"
                        className={`form-control${
                          formErrors.item_cost ? " error" : ""
                        }`}
                        placeholder="0.00"
                        value={formData.item_cost}
                        onChange={setField("item_cost")}
                      />
                      {formErrors.item_cost && (
                        <span className="form-error">
                          {formErrors.item_cost}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <input
                        className="form-control"
                        maxLength={3}
                        placeholder="CAD"
                        value={formData.currency}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            currency: e.target.value.toUpperCase(),
                          }))
                        }
                      />
                    </div>
                  </div>
                  {/* <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Min Order Qty</label>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        className="form-control"
                        placeholder="0"
                        value={formData.min_order_qty}
                        onChange={setField("min_order_qty")}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Prod. Time (seconds)</label>
                      <input
                        type="number"
                        min="0"
                        className="form-control"
                        placeholder="0"
                        value={formData.prod_time_seconds}
                        onChange={setField("prod_time_seconds")}
                      />
                      {formData.prod_time_seconds > 0 && (
                        <span className="form-hint">
                          {fmtSeconds(Number(formData.prod_time_seconds))}
                        </span>
                      )}
                    </div>
                  </div> */}
                </div>

                {/* Section 4: Inventory Thresholds */}
                {/* <div className="form-section">
                  <h3 className="section-title">Inventory Thresholds</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Reorder Level</label>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        className="form-control"
                        placeholder="0"
                        value={formData.reorder_level}
                        onChange={setField("reorder_level")}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Reorder Qty</label>
                      <input
                        type="number"
                        min="0"
                        step="0.001"
                        className="form-control"
                        placeholder="0"
                        value={formData.reorder_qty}
                        onChange={setField("reorder_qty")}
                      />
                    </div>
                  </div>
                </div> */}

                {/* Section 5: Notes & Status */}
                <div className="form-section">
                  <h3 className="section-title">
                    <Tag size={13} className="section-icon" /> Additional
                    Details
                  </h3>
                  <div className="form-row full">
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Optional notes about this material…"
                        value={formData.description}
                        onChange={setField("description")}
                      />
                    </div>
                  </div>
                  <div className="form-row full">
                    <div className="form-group">
                      <label className="form-label">Internal Notes</label>
                      <textarea
                        className="form-control"
                        rows={2}
                        placeholder="Private notes not visible to staff…"
                        value={formData.internal_notes}
                        onChange={setField("internal_notes")}
                      />
                    </div>
                  </div>
                  <div className="toggle-group">
                    <span className="toggle-label-text">Mark as Active</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={formData.is_active}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            is_active: e.target.checked,
                          }))
                        }
                      />
                      <span className="toggle-track" />
                    </label>
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving…"
                      : editingItem
                      ? "Save Changes"
                      : "Add Material"}
                  </button>
                </div>
              </div>
              {/* <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? "Saving…" : editingItem ? "Save Changes" : "Add Material"}
                </button>
              </div> */}
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewItem && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target.className === "modal-overlay") setShowViewModal(false);
          }}
        >
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <div className="modal-header">
              <div className="modal-header-content">
                <h2 className="modal-title">{viewItem.name}</h2>
                {viewItem.item_code && (
                  <p className="modal-subtitle">Code: {viewItem.item_code}</p>
                )}
              </div>
              <button
                className="btn-close"
                onClick={() => setShowViewModal(false)}
              >
                <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M15 5L5 15M5 5L15 15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {[
                {
                  label: "Item Type",
                  value:
                    ITEM_TYPE_LABELS[viewItem.item_type] ||
                    viewItem.item_type ||
                    "—",
                },
                { label: "Item Group", value: viewItem.item_group || "—" },
                { label: "Barcode", value: viewItem.barcode || "—" },
                {
                  label: "Procurement",
                  value: PROCUREMENT_LABELS[viewItem.procurement_type] || "—",
                },
                { label: "Base Unit", value: viewItem.base_unit || "—" },
                {
                  label: "Alt Unit",
                  value: viewItem.alt_unit
                    ? `${viewItem.alt_unit} (×${viewItem.conversion_factor})`
                    : "—",
                },
                { label: "Size Variant", value: viewItem.size_variant || "—" },
                {
                  label: "Cost / Unit",
                  value: `${fmt(viewItem.item_cost)} ${
                    viewItem.currency || ""
                  }`,
                },
                // { label: "Min Order", value: viewItem.min_order_qty || "—" },
                // {
                //   label: "Reorder Level",
                //   value: viewItem.reorder_level || "—",
                // },
                // { label: "Reorder Qty", value: viewItem.reorder_qty || "—" },
                // {
                //   label: "Prod. Time",
                //   value: fmtSeconds(viewItem.prod_time_seconds),
                // },
                {
                  label: "Status",
                  value: isActive(viewItem) ? "Active" : "Inactive",
                },
                { label: "Description", value: viewItem.description || "—" },
                {
                  label: "Internal Notes",
                  value: viewItem.internal_notes || "—",
                },
              ].map(({ label, value }) => (
                <div
                  key={label}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "9px 0",
                    borderBottom: "1px solid #f1f5f9",
                  }}
                >
                  <span
                    style={{
                      fontSize: 12,
                      color: "#64748b",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    {label}
                  </span>
                  <span
                    style={{
                      fontSize: 13.5,
                      color: "#1e293b",
                      fontWeight: 500,
                      textAlign: "right",
                      maxWidth: "60%",
                    }}
                  >
                    {value}
                  </span>
                </div>
              ))}
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  setShowViewModal(false);
                  openEdit(viewItem);
                }}
              >
                <Pencil size={13} /> Edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirm Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-modal-content">
            <div className="delete-modal-header">
              <div className="delete-icon-wrapper">
                <Trash2 size={22} />
              </div>
              <h3 className="delete-modal-title">Archive Material?</h3>
              <p className="delete-modal-text">
                This material will be archived and hidden from active lists. You
                can restore it later.
              </p>
            </div>
            <div className="delete-modal-footer">
              <button
                className="btn-cancel-delete"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                onClick={handleDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? "Archiving…" : "Yes, Archive"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirm Modal */}
      {showRestoreModal && (
        <div className="modal-overlay">
          <div className="delete-modal-content">
            <div className="delete-modal-header">
              <div
                className="delete-icon-wrapper"
                style={{ background: "#f0fdf4", color: "#059669" }}
              >
                <RotateCcw size={22} />
              </div>
              <h3 className="delete-modal-title">Restore Material?</h3>
              <p className="delete-modal-text">
                This material will be restored and made active again.
              </p>
            </div>
            <div className="delete-modal-footer">
              <button
                className="btn-cancel-delete"
                onClick={() => setShowRestoreModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn-confirm-delete"
                style={{ background: "#059669" }}
                onClick={handleRestore}
                disabled={restoreLoading}
              >
                {restoreLoading ? "Restoring…" : "Yes, Restore"}
              </button>
            </div>
          </div>
        </div>
      )}



      {/* Audit Trail Modal */}
{showAuditModal && (
  <div
    className="modal-overlay"
    onClick={(e) => {
      if (e.target.className === "modal-overlay") setShowAuditModal(false);
    }}
  >
    <div className="modal-content" style={{ maxWidth: 540 }}>
      <div className="modal-header">
        <div className="modal-header-content">
          <h2 className="modal-title">Audit Trail</h2>
          <p className="modal-subtitle">Change history for this material</p>
        </div>
        <button className="btn-close" onClick={() => setShowAuditModal(false)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <div className="modal-body">
        {auditLoading ? (
          <div style={{ textAlign: "center", padding: "32px", color: "#94a3b8" }}>
            <div className="spinner-border spinner-border-sm" style={{ width: 20, height: 20 }} />
            <div style={{ marginTop: 10, fontSize: 13 }}>Loading logs…</div>
          </div>
        ) : auditLogs.length === 0 ? (
          <div className="empty-state" style={{ padding: "32px" }}>
            <p className="empty-text">No audit logs found.</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {auditLogs.map((log, idx) => (
              <div
                key={log.id}
                style={{
                  padding: "12px 0",
                  borderBottom: idx < auditLogs.length - 1 ? "1px solid #f1f5f9" : "none",
                }}
              >
 
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span
                    className={`status-badge ${
                      log.action === "created" ? "active"
                      : log.action === "archived" ? "archived"
                      : log.action === "restored" ? "active"
                      : "inactive"
                    }`}
                  >
                    <span className="status-dot" />
                    {log.action.charAt(0).toUpperCase() + log.action.slice(1)}
                  </span>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>
                    {new Date(log.performed_at).toLocaleString("en-IN", {
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                </div>


                {log.changed_fields?.length > 0 && (
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>
                    Fields: <strong>{log.changed_fields.join(", ")}</strong>
                  </div>
                )}


                {log.old_values && log.new_values &&
                  log.changed_fields?.map((field) => (
                    <div
                      key={field}
                      style={{
                        display: "flex", alignItems: "center", gap: 6,
                        fontSize: 12, marginTop: 3,
                      }}
                    >
                      <span style={{ color: "#94a3b8", minWidth: 80, fontWeight: 500 }}>
                        {field}:
                      </span>
                      <span style={{
                        background: "#fef2f2", color: "#dc2626",
                        padding: "1px 6px", borderRadius: 4,
                        textDecoration: "line-through",
                      }}>
                        {String(log.old_values[field] ?? "—")}
                      </span>
                      <span style={{ color: "#94a3b8" }}>→</span>
                      <span style={{
                        background: "#f0fdf4", color: "#059669",
                        padding: "1px 6px", borderRadius: 4,
                      }}>
                        {String(log.new_values[field] ?? "—")}
                      </span>
                    </div>
                  ))
                }
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn-secondary" onClick={() => setShowAuditModal(false)}>
          Close
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
};

export default MaterialsManagement;
